<template>
  <div>
    <add-dialog v-if="dialog" v-model="dialog" :project="project"></add-dialog>
    <TableColumnSelector
      :show="showTableColumnSelector"
      @hide="showTableColumnSelector = false"
      v-model="headers"
      :skip="['actions']"
    ></TableColumnSelector>

    <!--    <v-btn>Validate annotations</v-btn>-->
    <div class="text-h3 mb-4">
      {{ $t("projects.slideManagement.title") }}
      <div class="subtitle-1">
        <span class="grey--text mb-2">
          {{ $t("projects.slideManagement.total") }}: 
        </span>
        <span class="font-weight-medium">{{ total }}</span>
      </div>
    </div>
    <v-toolbar flat class="mt-5">
      <v-select
        :label="$t('projects.slideManagement.bulkActionLabel')"
        :disabled="selectedSlides.length === 0"
        :items="bulkActionItems"
        v-model="selectedBulkAction"
        return-object
      ></v-select>
      <v-btn
        text
        :disabled="selectedSlides.length === 0 || selectedBulkAction === null"
        @click="onClickApplyBulkAction"
      >
        {{ $t("button.apply") }}
      </v-btn>
      <v-divider vertical class="mx-3" />
      <v-btn text @click="dialog = true">
        <v-icon>mdi-plus</v-icon>
          {{ $t("button.add") }}
        </v-btn>
      <v-spacer />
    </v-toolbar>
    <v-data-table
      v-model="selectedSlides"
      class="table"
      :headers="selectedHeaders"
      :items="list"
      :items-per-page="2"
      :loading="loading"
      :loading-text="$t('projects.slideManagement.table.loading')"
      :no-data-text="$t('projects.slideManagement.table.empty')"
      disable-filtering
      disable-sort
      @click:row="onClick"
      item-key="id"
      show-select
      :footer-props="footerProps"
      :server-items-length="total"
      :options.sync="options"
    >
      <template v-slot:header.actions="{}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              small
              @click.stop.prevent="showTableColumnSelector = true"
            >
              mdi-pencil
            </v-icon>
          </template>
          <span>
            {{ $t("components.tableColumnSelector.tooltip") }}
          </span>
        </v-tooltip>
      </template>
      <template v-slot:item.created="{ item }">
        {{ item.created | formatDate }}
      </template>
      <template v-slot:item.status="{ item }">
        {{ getStatus(item) }}
      </template>
      <template v-slot:item.organization="{ item }">
        {{ getOrganizationName(item) }}
      </template>
      <template v-slot:item.diagnose="{ item }">
        {{
          $dp.getFromExtension(
            fhir("ImagingStudy/" + item.imagingStudy),
            "http://deeppath.cl/fhir/StructureDefinition/DiagnoseExt",
            "valueCoding"
          ).system
        }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{}">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  large
                  @click.stop.prevent="slideOptionMenu = true"
                >
                  mdi-dots-horizontal
                </v-icon>
              </template>
              <v-list>
                <v-list-item @click="onClickView(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-eye-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ $t("projects.slideManagement.table.actions.view") }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="onClickValidate(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-eye-check-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ $t("projects.slideManagement.table.actions.validate") }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="onClickRequestAnnotations(item)">
                  <v-list-item-icon>
                    <v-icon></v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ $t("projects.slideManagement.table.actions.status") }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="onClickExtractROIs(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-cloud-download-outline </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ $t("projects.slideManagement.table.actions.extractROIs") }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="deleteItem(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ $t("projects.slideManagement.table.actions.remove") }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <span>Options</span>
        </v-tooltip>
      </template>
      <template v-slot:item.wsi.created="{ item }">
        {{ item.wsi.created | formatDate }}
      </template>
    </v-data-table>

    <dialog-delete
      v-model="dialogDelete"
      :item="itemDelete"
      @delete="onItemDeleted"
    ></dialog-delete>
    <annotation-class-dialog
      v-if="showAnnotationClassDialog"
      :project="project"
      :slide="showAnnotationClassDialogSlide"
      v-model="showAnnotationClassDialog"
    ></annotation-class-dialog>
    <ExtractROIDialog
      v-if="extractROIsDialog"
      :slide="extractROIsDialogModel"
      :dialog="extractROIsDialog"
      @hide="extractROIsDialog = false"
    ></ExtractROIDialog>
  </div>
</template>

<script>
import AddDialog from "@/components/projects/slide-management/AddDialog";
import DialogDelete from "@/components/DialogDelete";
import TableColumnSelector from "@/components/TableColumnSelector";
import { TABLE_HEADER_SLIDE_MANAGEMENT } from "@/constants";
import { mapGetters, mapState } from "vuex";
import AnnotationClassDialog from "@/components/projects/slide-management/AnnotationClassDialog";
import { SET_CURRENT_SLIDE } from "@/store/modules/annotation";
import {
  DELETE_SLIDE,
  FIND,
  LIST,
  LOAD_SLIDES,
  TOTAL
} from "@/store/modules/slides";
import { FIND as FIND_STATUS } from "@/store/modules/status";
import ExtractROIDialog from "@/components/projects/slide-management/ExtractROIDialog";
import { LOAD } from "@/store/modules/annotationClass";
import { FIND as FIND_FHIR, LOAD as LOAD_FHIR } from "@/store/modules/fhir";

export default {
  props: {
    project: { required: true }
  },
  data: () => ({
    selectedSlides: [],
    slideOptionMenu: false,
    selectedBulkAction: null,
    dialog: false,
    dialogDelete: false,
    itemDelete: null,
    showTableColumnSelector: false,
    showAnnotationClassDialog: false,
    showAnnotationClassDialogSlide: null,
    extractROIsDialog: false,
    extractROIsDialogModel: null,
    options: undefined
  }),
  computed: {
    ...mapState({
      loading: state => state.slides.loading
    }),
    ...mapGetters({
      total: TOTAL,
      list: LIST,
      fhir: FIND_FHIR
    }),
    bulkActionItems() {
      return [
        {
          text: this.$t("projects.slideManagement.bulkActions.delete"),
          id: "delete"
        },
        {
          text: this.$t("projects.slideManagement.bulkActions.requestAnnotations"),
          id: "request-annotations"
        }
      ]
    },
    selectedHeaders() {
      return this.headers.filter(e => e.visible);
    },
    footerProps() {
      return {
        "items-per-page-options": [5, 10, 15],
        "items-per-page-text": this.$t("projects.slideManagement.table.footerText")
      }
    },
    headers() {
      return [
        {
          text: this.$t("projects.slideManagement.table.id"),
          value: "id",
          visible: false
        },
        {
          text: this.$t("projects.slideManagement.table.dateAdded"),
          value: "created",
          visible: true
        },
        {
          text: this.$t("projects.slideManagement.table.status"),
          value: "status",
          visible: true
        },
        {
          text: this.$t("projects.slideManagement.table.diagnose"),
          value: "diagnose",
          visible: true
        },
        {
          text: this.$t("projects.slideManagement.table.manufacturerModel"),
          value: "scanner",
          visible: false
        },
        {
          text: this.$t("projects.slideManagement.table.organization"),
          value: "organization",
          visible: true
        },
        {
          text: this.$t("projects.slideManagement.table.numberOfAnnotations"),
          value: "number_of_annotations",
          visible: true
        },

        {
          text: this.$t("projects.slideManagement.table.actions.title"),
          value: "actions",
          visible: true
        }
      ]
    }
  },
  watch: {
    headers: {
      deep: true,
      handler(val) {
        this.$dp.store(TABLE_HEADER_SLIDE_MANAGEMENT, val, true);
      }
    },
    options(newVal, oldVal) {
      // do not search if rendered the first time
      if (oldVal) {
        if (
          newVal.itemsPerPage !== oldVal.itemsPerPage ||
          newVal.page !== oldVal.page
        ) {
          this.$store.dispatch(LOAD_SLIDES, newVal);
        }
      }
    }
  },
  methods: {
    onClick(slide) {
      this.onClickView(slide);
    },
    deleteItem(slides) {
      this.itemDelete = slides;
      this.dialogDelete = true;
    },
    onClickView(item) {
      const slide = {
        id: item.id,
        fhir_identifier: item.fhir_identifier,
        status: "annotations_requested",
        project: this.project.id
      };
      slide.annotationClassesRequested = item.wholeslideimagestatus_set;
      this.$store.dispatch(SET_CURRENT_SLIDE, {
        slide
      });
      this.$router.push({ name: "slide-view" });
    },
    onClickValidate(item) {
      const slide = {
        id: item.id,
        fhir_identifier: item.fhir_identifier,
        status: "annotations_requested",
        project: Object.assign({}, this.project)
      };
      slide.project.annotation_class = null;
      slide.annotation_class = item.wholeslideimagestatus_set
        .filter(e => e.status === "annotations_requested")
        .map(e => e.annotation_class);
      this.$store.dispatch(SET_CURRENT_SLIDE, {
        slide
      });
      this.$router.push({ name: "annotator-validation" });
    },
    async onItemDeleted(slides) {
      if (!Array.isArray(slides)) {
        slides = [slides];
      }
      await this.$store.dispatch(DELETE_SLIDE, slides);
      this.notify();
    },
    notify() {
      this.$notify({
        text: "Action completed.",
        type: "success"
      });
    },
    onClickApplyBulkAction() {
      // this.selectedBulkAction();
      console.log(this.selectedBulkAction);
      if (this.selectedBulkAction.id === "delete") {
        this.bulkDelete();
      }
    },
    bulkDelete() {
      this.deleteItem(this.selectedSlides);
      this.selectedSlides = [];
      this.selectedBulkAction = null;
    },
    onClickRequestAnnotations(slide) {
      this.showAnnotationClassDialogSlide = slide;
      this.showAnnotationClassDialog = true;
    },
    getStatus(slide) {
      const statusSet = new Set();

      slide.wholeslideimagestatus_set
        .map(e => this.$store.getters[FIND_STATUS](e))
        .forEach(e => statusSet.add(e));

      console.log(statusSet);
      // if set has more than 1 item, slides have different status so declare it partially...
      if (statusSet.size === 1) {
        return [...statusSet][0];
      } else if (statusSet.size === 0) {
        return "Assigned";
      } else {
        // TODO eventually prioritize different newStatus --> assigned, requested, ...
        return `Partially ${this.$dp.mapSlideStatus(
          [...statusSet][statusSet.size - 1]
        )}`;
      }
    },
    onUpdateOptions(val) {
      console.log(val);
    },
    onClickExtractROIs(slide) {
      this.extractROIsDialogModel = slide;
      this.extractROIsDialog = true;
    },
    getOrganizationName(item) {
      const organizationReference = this.fhir(
        "ImagingStudy/" + item.imagingStudy
      ).organizationResource.reference;
      if (organizationReference) {
        const organization = this.fhir(organizationReference);
        if (organization) {
          return organization.name;
        }
      }
      return "";
    }
  },
  async mounted() {
    this.headers = this.$dp.read(TABLE_HEADER_SLIDE_MANAGEMENT, this.headers);
  },
  async created() {
    // load annotation classes as needed anyway
    this.$store.dispatch(LOAD);
    // load slides
    await this.$store.dispatch(LOAD_SLIDES);
    // load organization resources as required for displaying
    this.list.forEach(e => {
      const is = this.fhir(`ImagingStudy/${e.imagingStudy}`);
      this.$store.dispatch(LOAD_FHIR, is.organizationResource);
    });
  },
  components: {
    ExtractROIDialog,
    AnnotationClassDialog,
    TableColumnSelector,
    DialogDelete,
    AddDialog
  }
};
</script>

<style lang="scss" scoped></style>
